import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { PermissionMessageService } from '../services/permission-message.service';
import { SnackbarService } from '../services/snackbar.service';
import { ENUM_PERMISSIONS } from '../enums/permission.enum';
import { PERMISSION_UI_PATTERN } from '../config/permission-ui.config';

/**
 * Garde standard des boutons d'action : bloque un élément quand l'utilisateur n'a AUCUNE des
 * permissions demandées (logique OU). Le rendu dépend de PERMISSION_UI_PATTERN
 * (core/config/permission-ui.config.ts) :
 * - 'hide' : l'élément est masqué (display: none).
 * - 'lock' : l'élément reste visible mais verrouillé — classe `permission-locked` (cadenas +
 *   cursor not-allowed), tooltip « Permission requise : ... », clic intercepté avant les handlers
 *   du composant pour afficher la snackbar explicative.
 *
 * À la différence de *hasPermission (masquage définitif, réservé au structurel : onglets,
 * sections, routes), cette directive suit le pattern global et permet de basculer toute
 * l'application entre hide et cadenas en changeant une constante.
 *
 * Usage : <button mat-raised-button [permissionBlock]="[ENUM_PERMISSIONS.UPDATE_TICKET]" (click)="...">
 */
@Directive({
  selector: '[permissionBlock]',
})
export class PermissionBlockDirective implements OnInit, OnDestroy {
  private requiredPermissions: ENUM_PERMISSIONS[] = [];
  private blocked = false;
  private permissionsSubscription: Subscription;

  // Capture au niveau document : garantit de passer AVANT les handlers (click) posés sur
  // l'élément lui-même, quel que soit leur ordre d'enregistrement.
  private readonly interceptClick = (event: Event) => {
    if (!this.blocked || !this.element.nativeElement.contains(event.target)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.snackbarService.danger(this.permissionMessageService.buildMissingPermissionMessage(this.requiredPermissions));
  };

  constructor(
    private element: ElementRef,
    private renderer: Renderer2,
    private authService: AuthService,
    private permissionMessageService: PermissionMessageService,
    private snackbarService: SnackbarService,
  ) {}

  @Input()
  set permissionBlock(value: ENUM_PERMISSIONS | ENUM_PERMISSIONS[]) {
    this.requiredPermissions = value ? (Array.isArray(value) ? value : [value]) : [];
    this.updateState(this.authService.getCurrentUserPermissions());
  }

  ngOnInit(): void {
    document.addEventListener('click', this.interceptClick, true);
    this.permissionsSubscription = this.authService.getCurrentUserPermissions$().subscribe(userPermissions => {
      this.updateState(userPermissions as ENUM_PERMISSIONS[]);
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.interceptClick, true);
    this.permissionsSubscription?.unsubscribe();
  }

  private updateState(userPermissions: ENUM_PERMISSIONS[]): void {
    const allowed =
      this.requiredPermissions.length === 0 ||
      this.requiredPermissions.some(permission => userPermissions?.includes(permission));

    this.blocked = !allowed;

    if (this.blocked) {
      if (PERMISSION_UI_PATTERN === 'hide') {
        this.renderer.setStyle(this.element.nativeElement, 'display', 'none');
      } else {
        this.renderer.addClass(this.element.nativeElement, 'permission-locked');
        this.renderer.setAttribute(
          this.element.nativeElement,
          'title',
          this.permissionMessageService.buildMissingPermissionMessage(this.requiredPermissions),
        );
      }
    } else {
      this.renderer.removeStyle(this.element.nativeElement, 'display');
      this.renderer.removeClass(this.element.nativeElement, 'permission-locked');
      this.renderer.removeAttribute(this.element.nativeElement, 'title');
    }
  }
}
